import { getSupabaseClient } from './supabase';

interface ApiRequestOptions extends RequestInit {
  requireAuth?: boolean;
}

export async function apiRequest(
  url: string,
  options: ApiRequestOptions = {}
): Promise<Response> {
  const { requireAuth = true, headers = {}, ...fetchOptions } = options;

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(headers as Record<string, string>),
  };

  // Add bearer token if auth is required
  if (requireAuth) {
    const supabase = getSupabaseClient();
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session?.access_token) {
      requestHeaders['Authorization'] = `Bearer ${session.access_token}`;
    }
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers: requestHeaders,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response;
}

export async function apiGet<T>(url: string, requireAuth = true): Promise<T> {
  const response = await apiRequest(url, { requireAuth });
  return response.json();
}

export async function apiPost<T>(
  url: string,
  data: unknown,
  requireAuth = true
): Promise<T> {
  const response = await apiRequest(url, {
    method: 'POST',
    body: JSON.stringify(data),
    requireAuth,
  });
  return response.json();
}

export async function apiPut<T>(
  url: string,
  data: unknown,
  requireAuth = true
): Promise<T> {
  const response = await apiRequest(url, {
    method: 'PUT',
    body: JSON.stringify(data),
    requireAuth,
  });
  return response.json();
}

export async function apiDelete<T>(url: string, requireAuth = true): Promise<T> {
  const response = await apiRequest(url, {
    method: 'DELETE',
    requireAuth,
  });
  return response.json();
}
